import React, { useEffect, useRef, useState } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import i18n from 'i18next'
import * as yup from 'yup'
import isEqual from 'lodash/isEqual'
import { yupResolver } from '@hookform/resolvers/yup'
import Yup from 'yup'
import { Controller, useForm, FormProvider } from 'react-hook-form'
import type { FieldValues } from 'react-hook-form'

import {
  Button,
  Checkbox,
  Container,
  FormHelperText,
  TextField,
} from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import type { Theme } from '@mui/material/styles'

import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import type { SetupIntent } from '@stripe/stripe-js'

import { Caption, Text1 } from 'ui/shorthands/Texts'
import {
  StackItemTextField,
  useStyles as textFieldStyles,
} from 'ui/shorthands/StackItemTextField'
import validationSchema from 'ui/shorthands/validationSchema'

import { StackItem } from 'ui/layout/StackItem'
import { Layout } from 'ui/layout/Layout'
import { AppBarPatient } from 'ui/partials/AppBar'
import OnMousePopover from 'ui/partials/OnMousePopover'

import { EditableThumbnailList } from 'ui/utils/Thumbnails'
import StripeCardInputs, { StripeErrorProps } from 'ui/utils/StripeCardInputs'
import SelectDate from 'ui/utils/SelectDate'
import { toDateDayTimeStringFromDateTimeString } from 'ui/utils/AvailableDateTimes'
import { ReservationDateSelectProps } from 'ui/utils/ReservationDateSelect'
import SelectDepartment from './SelectDepartment'
import { jstNewDate, endOfJstToDay } from 'ui/utils/jstDatetime'

import {
  HowToReceiveMedicineKeys,
  MedicalInstitutionProps,
  PersonalSettlementsNames,
  ReservationFormProps,
  StripeSettingProps,
  EligibilityVerificationUrlSettingProps,
  InsuranceType,
  PharmacyServiceType,
} from 'types/applicant'
import type {
  DepartmentSettingsProps,
  PrescriptionSetting,
  ReservationFrameProps,
  SelectOptionsProps,
} from 'types/med'
import type { PharmacyProps, SettlementNameProps } from 'types/common'

import { DateString } from 'config/format'
import { addDays, isBefore, addMinutes, parse } from 'date-fns'
import {
  formatDateTimeSecondString,
  parseDatetimeSecondString,
} from 'ui/utils/dateFormatUtils'
import { StackItemRadioSelect } from 'ui/shorthands/StackItemRadioSelect'
import {
  QuestionTooltipWithLabel,
  UploadQuestionTooltipMessage,
} from 'ui/shorthands/QuestionTooltip'
import Modal from 'ui/layout/Modal'
import PharmacySearch, {
  PharmacySearchProps,
} from 'ui/organisms/PharmacySearch'
import theme from 'ui/theme'
import PharmacyInfo from 'ui/partials/PharmacyInfo'
import DeleteButton from 'ui/partials/DeleteButton'
import LinkUrlText from 'ui/utils/LinkUrlText'
import placeholder from 'config/placeholder'
import { CallMethodNames, ImageProps } from 'types/common'
import { MethodProps } from 'types/med'
import ApplicantAddressInputForm from 'ui/organisms/ApplicantAddressInputForm'
import ReservationFrameSelectForm from 'ui/organisms/ReservationFrameSelectForm'
import { SendingContent, SendingContentError } from 'ui/partials/ConfirmContent'
import { ApiErrorWithMessage, NetworkError, TimeoutError } from 'app/client'
import { isPastJstNow } from 'ui/utils/jstDatetime'
import TodokusuriText, {
  howToReceiveMedicineDirectValue,
} from 'ui/partials/TodokusuriText'
import RadioSelectContents, {
  RadioSelectContentsProps,
} from 'ui/organisms/RadioSelectContents'
import {
  EligibilityVerificationButton,
  EligibilityVerificationMessageApi,
} from 'app/personal/OnlineEligibilityVerification'

const callMethodLabels: {
  [key in CallMethodNames]: 'Zoom' | 'Microsoft Teams' | '電話'
} = {
  video_zoom: 'Zoom',
  video_teams: 'Microsoft Teams',
  phone: '電話',
}

const PersonalSettlementsNamesWithLAbel: React.FC<{
  name: SettlementNameProps
}> = ({ name }) => {
  const { t } = useTranslation()
  const label = t(PersonalSettlementsNames[name])
  switch (name) {
    case 'cod':
      return (
        <QuestionTooltipWithLabel label={label}>
          {t('お薬など発送物がある場合のみ選択してください。')}
        </QuestionTooltipWithLabel>
      )
    default:
      return <span>{label}</span>
  }
}

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
  },
  category: {
    marginTop: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    '&:last-child': {
      marginBottom: theme.spacing(4),
    },
  },
  editCreditButtonWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  submitWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'flex-end',
      display: 'grid',
      gridTemplateColumns: '1fr auto',
    },
  },
  leftContainer: {
    flex: 1,
  },
  submitButton: {
    marginLeft: theme.spacing(2),
  },

  topCheckbox: {
    paddingLeft: 0,
  },
  preWrap: {
    whiteSpace: 'pre-wrap',
  },
  text: {
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('xs')]: {
      gridRow: '1 / 2',
      gridColumn: '1 / 3',
    },
  },
  reservationDatetimeStart: {
    backgroundColor: theme.palette.background.paper,
  },
  radioGroup: {
    display: 'block',
  },
  pharmacy: {
    position: 'relative',
    '&:not(:last-child)': {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  pharmacySelectCaption: {
    paddingLeft: theme.spacing(2),
  },
  pharmacyDescription: {
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2, 2, 2, 0),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2),
    },
  },
  pharmacySelectButtons: {
    padding: theme.spacing(2, 0, 1),
  },
  pharmacyContent: {
    marginLeft: theme.spacing(2),
    paddingBottom: theme.spacing(1),
  },
  selectPharmacyContentInSelection: {
    marginRight: theme.spacing(-2),
  },
  eligibilityVerification: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
}))

const getFirstActiveMethod: (
  settings?: MethodProps[]
) => MethodProps | undefined = (settings) => settings?.find((s) => s.is_active)

const isActiveMethod: (
  settlementMethodName: SettlementNameProps,
  settings?: MethodProps[]
) => boolean = (settlementMethodName, settings) =>
  !!settings?.find((s) => s.is_active && s.name === settlementMethodName)

export const howToReceiveMedicineValues: {
  label: '取りに行く' | '配送'
  value: HowToReceiveMedicineKeys
}[] = [
  { label: '取りに行く', value: 'visit' },
  { label: '配送', value: 'delivery' },
]

const selectPharmacyOptionTodokusuriValue = {
  label: <TodokusuriText />,
  value: 'todokusuri',
  description: (
    <Caption>
      {i18n.t(
        'とどくすりを利用するには会員登録（無料）が必要です。また電話番号情報を、医療機関からとどくすりに共有させていただきます。ご了承ください。'
      )}
    </Caption>
  ),
}

export const TayorisContent = () => (
  // NOTE: Pocなので日本語表示のみ
  <>
    <div>
      <Text1>そうごう薬局 蔵前ＪＰテラス店</Text1>
    </div>
    <Caption>
      ※お薬の受け取りはタヨリスからご予約ください
      <br />
      {'　'}
      薬局は「そうごう薬局
      蔵前ＪＰテラス店」を選択してください（お薬の配送料は無料です）
    </Caption>
  </>
)

export type ReservationProps = {
  /**
   * 医療機関データ
   */
  med: MedicalInstitutionProps
  /**
   * 診療科設定のid
   */
  departmentId: number
  /**
   * 診療科を選択した時
   * @param event
   */
  onSelectDepartment: (event: React.ChangeEvent<HTMLInputElement>) => void
  /**
   * 選択された診療科の予約設定
   */
  settings?: DepartmentSettingsProps
  /**
   * 予約日を選択した時
   * @param date
   */
  onSetDate: (date: Date) => void
  /**
   * 選択された月の予約可能日
   */
  reservableDates: ReservationDateSelectProps['reservableDates']
  /**
   * 選択された月の予約可能日
   */
  onChangeCalenderMonth: ReservationDateSelectProps['onChangeCalenderMonth']
  /**
   * 日付に対応した予約枠リスト
   */
  reservationFrames?: ReservationFrameProps[]
  /**
   * 送信ボタンを押した時
   */
  onSubmit: (data: ReservationFormProps) => Promise<void>
  /**
   * キャンセルボタンを押した時
   */
  onCancel: () => void
  /**
   * 編集モードの時にセットする既存値
   */
  defaultValues?: ReservationFormProps
  /**
   * 決済方法でstripeを選択したときに発火 stripeSettingをセットする
   */
  onSelectStripe?: () => Promise<StripeSettingProps>
  /**
   * 配送設定
   */
  prescription: {
    setting?: PrescriptionSetting
    nearPharmacies: PharmacyProps[]
    apiKey: PharmacySearchProps['apiKey']
    onSearch: PharmacySearchProps['onSearch']
  }
  /**
   * オンライン資格確認URL生成のための設定値
   */
  eligibilityVerificationUrlSetting?: EligibilityVerificationUrlSettingProps
}

const imagesSelectOptionValidation = (selectOption: SelectOptionsProps) => {
  switch (selectOption) {
    case 'required':
      return yup.array().min(1).required()
    default:
      return yup.array().nullable()
  }
}

const getMaxDateMessage = (settings: DepartmentSettingsProps): string =>
  i18n.t('予約可能な期間は当日から{{num}}週間先までです', {
    num: settings.reservable_period / 7,
  })

const getMaxDate = (settings: DepartmentSettingsProps) =>
  addDays(endOfJstToDay, settings.reservable_period - 1)

const Reservation: React.FC<ReservationProps> = ({
  med,
  departmentId,
  settings,
  onSubmit,
  defaultValues,
  onSelectDepartment,
  onSetDate,
  reservableDates,
  onChangeCalenderMonth,
  reservationFrames,
  onSelectStripe,
  onCancel,
  prescription,
  eligibilityVerificationUrlSetting,
}: ReservationProps) => {
  const { t } = useTranslation()
  const { classes, cx } = useStyles()
  const textFieldClasses = textFieldStyles().classes

  const [selectedDate, setSelectedDate] = useState<Date | undefined>()
  const [saveCreditInfo, setSaveCreditInfo] = useState<boolean>(false)
  const [pharmacySelectable, setPharmacySelectable] = useState<boolean>(true)
  const [stripeSucceeded, setStripeSucceeded] = useState<boolean>(false)
  const [stripeSubmitting, setStripeSubmitting] = useState<boolean>(false)
  const [sending, setSending] = useState<boolean>(false)
  const [stripeSetting, setStripeSetting] = useState<StripeSettingProps>()
  const [reservationDepartmentId, setReservationDepartmentId] =
    useState<number>(departmentId)

  const [sendingContentError, setSendingContentError] = useState<
    SendingContentError | undefined
  >()

  const [showPharmacySearchModal, setShowPharmacySearchModal] = useState<
    'search' | 'preset' | undefined
  >()

  const [insuranceType, setInsuranceType] = useState<InsuranceType | undefined>(
    undefined
  )

  const calcOtherDate = (datetimeString: string, addMin: number) => {
    return addMinutes(parseDatetimeSecondString(datetimeString), addMin)
  }

  const onSelectStripeRef = useRef(onSelectStripe)

  const isPreReservation = () =>
    !defaultValues?.id && !!defaultValues?.reservation_datetime_start

  const myNumberInsuranceSelectable = !!eligibilityVerificationUrlSetting?.url

  const prescriptionMedicineSettingIsPharmacy =
    prescription.setting?.prescription_medicine === 'pharmacy'

  const prescriptionMedicineSettingIsDirect =
    prescription.setting?.prescription_medicine === 'direct'

  const prescriptionMedicineSettingIsOwn =
    prescription.setting?.prescription_medicine === 'own'

  const buildValidationSchema: (
    settings: DepartmentSettingsProps
  ) => Yup.AnyObjectSchema = (settings) => {
    return yup.object({
      access_key: yup.string(),
      reservation_department_id: yup.number().nullable(),
      reservation_datetime_start: yup
        .string()
        .required(t('validation.時間帯を選択してください'))
        .test(
          '開始日付',
          t('validation.過去の予約枠を選択することはできません'),
          (start) => {
            if (!start) return false
            // NOTE: 直前の仮予約の時
            if (isPreReservation()) {
              return !isPastJstNow(
                calcOtherDate(start, settings.reservation_frame_period_minutes)
              )
            }
            // NOTE: 通常予約の時
            return !isPastJstNow(parseDatetimeSecondString(start))
          }
        )
        .test('開始日付', getMaxDateMessage(settings), (start) => {
          if (!start) return false
          const maxAvailableDate = addDays(
            endOfJstToDay,
            settings.reservable_period
          )
          return isPreReservation() // NOTE: 仮予約の時は無視
            ? true
            : isBefore(parseDatetimeSecondString(start), maxAvailableDate)
        }),
      reservation_datetime_end: yup.string().required(),
      consultation_method: yup.lazy(() => {
        switch (settings.use_in_person_consultation) {
          case true:
            return validationSchema.textRequired()
          default:
            return yup.string().nullable()
        }
      }),
      call_method: yup.string().nullable(), // NOTE: 必要な時は必ず選択されるようになっているためnullable
      applicant_last_name: validationSchema.textRequired(),
      applicant_first_name: validationSchema.textRequired(),
      applicant_last_name_kana: validationSchema.textHiragana(),
      applicant_first_name_kana: validationSchema.textHiragana(),
      applicant_birthday: yup.string(),
      applicant_phone: validationSchema.phone().required(),
      settlement_method: getFirstActiveMethod(settings.settlement_settings)
        ? validationSchema.textRequired() //支払い方法がある時は選択肢の何か 'next_visit' | 'credit_card' | 'bank_transfer'
        : validationSchema.text(),
      saves_data: yup.boolean(),
      //  requiredが動的
      credit_name: validationSchema
        .textAlphabet()
        .max(100)
        .test('required', t('validation.入力してください'), function (name) {
          // NOTE: 支払い方法がcredit_cardの時かつデータ未保存であれば必須にする
          if (!saveCreditInfo) return true
          return !(this.parent.settlement_method === 'credit_card' && !name)
        }),
      free_description: yup.lazy(() => {
        const schema = yup.string().trim().max(400)
        switch (settings.free_description_field_setting) {
          case 'required':
            return schema.required()
          default:
            return schema.nullable()
        }
      }),
      applicant_postal_code: yup
        .object()
        .when(
          'how_to_receive_medicine',
          (how_to_receive_medicine: HowToReceiveMedicineKeys) =>
            how_to_receive_medicine === 'delivery' ||
            settings.applicant_address_setting === 'required'
              ? validationSchema.postalCode().nullable().required()
              : validationSchema.postalCode().nullable()
        ),
      applicant_address: yup
        .object()
        .when(
          'how_to_receive_medicine',
          (how_to_receive_medicine: HowToReceiveMedicineKeys) =>
            how_to_receive_medicine === 'delivery' ||
            settings.applicant_address_setting === 'required'
              ? validationSchema.text().nullable().required()
              : validationSchema.text().nullable()
        ),
      registration_card_number: yup.lazy(() => {
        switch (settings.registration_card_number_setting) {
          case 'required':
            return validationSchema.textRequired()
          default:
            return validationSchema.text()
        }
      }),
      // 画像系
      attachment_images: yup.lazy(() =>
        imagesSelectOptionValidation(settings.attachment_image_setting)
      ),
      identity_verification_images: yup.lazy(() =>
        imagesSelectOptionValidation(settings.identification_document_setting)
      ),
      insurance_card_images: yup.lazy(() => {
        // オンライン資格確認が選択可能な状態の時
        if (myNumberInsuranceSelectable) {
          switch (insuranceType) {
            case 'insurance_card': // 保険証を選択している場合のみ常に必須になる
              return yup.array().min(1).required()
            default:
              return yup.array().nullable()
          }
        } else {
          // オンライン資格確認が使えない状態だと選択肢が表示されないので常に他の添付ファイル同様になる
          return imagesSelectOptionValidation(settings.insurance_card_setting)
        }
      }),
      certificate_images: yup.lazy(() =>
        imagesSelectOptionValidation(settings.certificate_setting)
      ),
      how_to_receive_medicine: yup
        .object()
        .when(
          'pharmacy_service',
          (pharmacy_service: PharmacyServiceType | null) =>
            !prescriptionMedicineSettingIsOwn ||
            pharmacy_service === 'tayoris_health_management_support'
              ? yup.string().nullable()
              : yup.string().required()
        ),
      pharmacy_service: yup.string().nullable(),
      scuel_id: yup
        .object()
        .when(
          'pharmacy_service',
          (pharmacy_service: PharmacyServiceType | null) =>
            prescriptionMedicineSettingIsPharmacy && !pharmacy_service
              ? yup.string().required()
              : yup.string().nullable()
        ),
      stripe_is_valid: yup
        .object()
        .when('settlement_method', (settlement_method: SettlementNameProps) =>
          settlement_method === 'credit_card' && saveCreditInfo
            ? yup.boolean().oneOf([true], '')
            : yup.boolean().strip()
        ),
      online_eligibility_verification_agreed: yup.lazy(() => {
        // オンライン資格確認が選択可能な状態の時かつ、my_number_insurance選択されている場合は常に必須かつtrueのみ許可
        if (insuranceType === 'my_number_insurance') {
          return yup
            .boolean()
            .oneOf([true], t('validation.閲覧同意に失敗しました'))
            .required()
        }
        return yup.boolean().nullable()
      }),
    })
  }

  const formMethods = useForm<FieldValues>({
    defaultValues,
    resolver: yupResolver(
      settings ? buildValidationSchema(settings) : yup.object()
    ),
    mode: 'onChange',
  })
  const {
    handleSubmit,
    control,
    setError,
    clearErrors,
    setValue,
    getValues,
    reset,
    trigger,
    formState: { isDirty, isValid, errors },
    watch,
  } = formMethods

  const watchSettlementMethod = watch('settlement_method')
  const watchHowToReceiveMedicine = watch('how_to_receive_medicine')
  const watchReservationDatetimeStart = watch('reservation_datetime_start')
  const watchConsultationMethod = watch('consultation_method')
  const watchPharmacyService: PharmacyServiceType | '' | null =
    watch('pharmacy_service')
  const watchIsTayoris: boolean =
    watchPharmacyService === 'tayoris_health_management_support'
  const watchEligibilityAgreed = watch('online_eligibility_verification_agreed')

  const [selectedPharmacy, setSelectedPharmacy] = useState<
    PharmacyProps | undefined
  >(undefined)

  useEffect(() => {
    if (watchSettlementMethod === 'credit_card' && !stripeSetting) {
      isActiveMethod('credit_card', settings?.settlement_settings) &&
        onSelectStripeRef.current &&
        onSelectStripeRef
          .current()
          .then(setStripeSetting)
          .catch(() => {
            setError('settlement_method', {
              type: 'stripe_api_error',
              message: t(
                'errors.現在、クレジットカードによる決済が行えません。別の決済方法を選択してください。'
              ),
            })
          })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchSettlementMethod, stripeSetting, settings])

  useEffect(() => {
    if (stripeSetting) {
      setSaveCreditInfo(!stripeSetting?.payment_method_id)
      setValue('payment_method_id', stripeSetting.payment_method_id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripeSetting])

  const defaultValuesRef = useRef<ReservationFormProps | undefined | null>(null)

  useEffect(() => {
    // NOTE: 画像など非同期で遅れて読み込まれるデータをformにセットする
    if (isEqual(defaultValuesRef?.current, defaultValues)) return
    defaultValuesRef.current = defaultValues

    // NOTE: 全画像の読み込みが完了する前に日時を選択した場合の復帰処理
    // TODO: 個別対応なので他の全ての変更済みの値でも対応できるようにするべき、または読み込み終わるまで画面をロックするなど必要？
    const isEdit = defaultValues?.id
    const reservation_department_id =
      defaultValues?.reservation_department_id || reservationDepartmentId
    const reservation_datetime_start: string | undefined =
      getValues('reservation_datetime_start') ||
      defaultValues?.reservation_datetime_start
    const reservation_datetime_end: string | undefined =
      getValues('reservation_datetime_end') ||
      defaultValues?.reservation_datetime_end
    reset({
      ...defaultValues,
      reservation_department_id,
      reservation_datetime_start,
      reservation_datetime_end,
      consultation_method:
        defaultValues?.consultation_method ||
        (settings?.use_in_person_consultation ? 'online' : null),
      how_to_receive_medicine: prescriptionMedicineSettingIsOwn
        ? defaultValues?.how_to_receive_medicine
        : undefined,
    })

    const timer = setTimeout(() => {
      // NOTE: 日時指定の仮予約以外の新規予約で全画像の読み込みが完了する前に日時を選択した場合には、選択済みであることも復帰
      if (reservation_datetime_start && !isPreReservation()) {
        setValue('reservation_datetime_start', reservation_datetime_start, {
          shouldTouch: !isEdit,
        })
        setValue('reservation_datetime_end', reservation_datetime_end, {
          shouldTouch: !isEdit,
        })
      }

      if (!defaultValues?.reservation_department_id) {
        setValue('reservation_department_id', reservationDepartmentId)
      }
    }, 1)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues])

  useEffect(() => {
    setValue('reservation_department_id', reservationDepartmentId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservationDepartmentId])

  // NOTE: 通話方法,決済方法は前回の設定がなくてis_activeな通話方法が存在すれば
  // 科目ごとに一番最初の値が設定されている状態または設定なしにする
  function setAppropriateMethod(name: string, methods?: MethodProps[]): void {
    const currentMethod = getValues(name)
    const firstMethod = getFirstActiveMethod(methods)?.name

    if (!currentMethod) {
      setValue(name, firstMethod)
    } else if (currentMethod && !isActiveMethod(currentMethod, methods)) {
      setValue(name, firstMethod)
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      // NOTE: 科目を変えた時に存在しない値にならないようにリセット
      setAppropriateMethod('call_method', settings?.call_methods)
      setAppropriateMethod('settlement_method', settings?.settlement_settings)

      // NOTE: 科目を変えた時にuse_in_person_consultationの値によってconsultation_methodを更新する
      if (!settings?.use_in_person_consultation) {
        setValue('consultation_method', null)
      } else {
        setValue(
          'consultation_method',
          defaultValues?.consultation_method ?? 'online'
        )
      }

      // NOTE: 日時指定の仮予約では終了日時を基準に開始日時を非同期でセットし直す
      if (settings && defaultValues && isPreReservation()) {
        const end = getValues('reservation_datetime_end')
        const period = settings?.reservation_frame_period_minutes
        const start = formatDateTimeSecondString(calcOtherDate(end, -period))
        setValue('reservation_datetime_start', start)
      }
    }, 1)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, defaultValues])

  useEffect(() => {
    if (selectedDate) {
      onSetDate(selectedDate)
      onChangeCalenderMonth?.(selectedDate)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, departmentId])

  const onStripePostSuccess = (setupIntent: SetupIntent) => {
    clearErrors('settlement_method')
    setValue('payment_method_id', setupIntent.payment_method)
    setStripeSucceeded(true)
    setStripeSubmitting(false)
  }
  const onStripePostError = (error: StripeErrorProps | undefined) => {
    setStripeSubmitting(false)
    setSending(false)
    error
      ? setError('settlement_method', {
          type: error.type,
          message: error.message,
        })
      : clearErrors('settlement_method')
  }

  const submitErrorHandler = (e: Error) => {
    if (e instanceof ApiErrorWithMessage) {
      if (
        e.has({
          key: 'call_method',
        })
      ) {
        setError('call_method', {
          type: 'exists',
          message: t(
            'errors.医療機関により通話方法が削除されています。別の通話方法を選択してください。'
          ),
        })
        return
      }
      if (
        e.has({
          key: 'settlement_method',
        })
      ) {
        setError('settlement_method', {
          type: 'exists',
          message: t(
            'errors.医療機関により支払方法が削除されています。別の支払方法を選択してください。'
          ),
        })
        return
      }
      if (
        e.has({
          key: 'reservation_datetime_start',
          value: 'capacity',
        })
      ) {
        setError('reservation_datetime_start', {
          type: 'capacity',
          message: t(
            'errors.選択された時間は予約できませんでした。もう一度選択してください。'
          ),
        })
        return
      }
      if (
        e.has({
          key: 'reservation_datetime_start',
          value: 'valid',
        })
      ) {
        setError('reservation_datetime_start', {
          type: 'valid',
          message: t(
            'errors.予約可能日時が変更されました。別の時間帯を選択してください。'
          ),
        })
        return
      }
      if (
        e.has({
          key: 'reservation_datetime_start',
          value: 'deadline',
        })
      ) {
        setError('reservation_datetime_start', {
          type: 'deadline',
          message: t(
            'errors.予約日時が過ぎております。予約日時の変更をお願いします。'
          ),
        })
        return
      }

      // NOTE: 予約枠設定が変更されていた時
      if (
        e.has({
          key: 'reservation_time_period',
          value: 'valid',
        })
      ) {
        setValue('reservation_datetime_start', undefined)
        setValue('reservation_datetime_end', undefined)
        selectedDate && onSetDate(selectedDate)
        setTimeout(() => {
          setError('reservation_datetime_start', {
            type: 'valid',
            message: t(
              'errors.入力中に予約情報に変更がありました。再度入力の上送信をお願いいたします。'
            ),
          })
        }, 100)
        return
      }
    }

    if (e instanceof NetworkError) {
      setSendingContentError('network')
      return
    }
    if (e instanceof TimeoutError) {
      setSendingContentError('timeout')
      return
    }
    // NOTE: その他の予期しないエラー
    setSendingContentError('server')
  }

  const submitHandler = (data: FieldValues) => {
    setSending(true)
    // NOTE: Stripeにカードデータを送信するときはフォームを送信しない
    if (getValues('settlement_method') === 'credit_card' && saveCreditInfo) {
      setStripeSubmitting(true)
      return
    }
    // NOTE: 保険証の選択肢によって不要なデータを削除
    // TODO: 設定値と条件の組み合わせが複雑になってしまったので設定値と共に再考の余地があるかも
    if (
      insuranceType === 'not_use' ||
      insuranceType === 'my_number_insurance'
    ) {
      delete data.insurance_card_images
      if (insuranceType === 'not_use') {
        delete data.online_eligibility_verification_agreed
      }
    }
    onSubmit(data as ReservationFormProps)
      .catch(submitErrorHandler)
      .finally(() => {
        setSending(false)
      })
  }
  // NOTE: Stripeでカード保存成功したら再度Submit
  useEffect(() => {
    if (stripeSucceeded) {
      handleSubmit((data) => {
        if (data?.stripe_is_valid) {
          delete data.stripe_is_valid
        }
        return onSubmit(data as ReservationFormProps)
          .catch(submitErrorHandler)
          .finally(() => {
            setSending(false)
          })
      })()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripeSucceeded])

  useEffect(() => {
    !saveCreditInfo &&
      trigger('stripe_is_valid').then(() => {
        clearErrors('settlement_method')
        setValue('credit_name', undefined)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveCreditInfo])

  useEffect(() => {
    // NOTE: 保険証・マイナ保険証のフォームの形と挙動は設定値によって変わる
    // 条件変更の処理が複数箇所（バリデーション/選択フォーム/このデフォルト値設定箇所）に散らばっているので注意
    const isEdit = defaultValues?.id
    if (myNumberInsuranceSelectable) {
      // 予約済み編集画面の時
      if (isEdit && defaultValues?.online_eligibility_verification_agreed) {
        setInsuranceType('my_number_insurance')
      } else {
        // 新規予約画面の時
        if (settings?.insurance_card_setting === 'required') {
          const defaultInsuranceType = defaultValues?.insurance_card_type
          setInsuranceType(defaultInsuranceType ?? 'insurance_card')
        } else {
          setInsuranceType('not_use')
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligibilityVerificationUrlSetting, defaultValues])

  const onChangePharmacyHandler = (
    pharmacy: PharmacyProps | undefined,
    isDefault?: boolean
  ) => {
    setSelectedPharmacy(pharmacy)
    setValue('scuel_id', pharmacy?.scuel_id, {
      shouldDirty: !isDefault,
      shouldValidate: !isDefault,
    })
  }

  const shouldSelectHowToReceiveMedicine =
    prescriptionMedicineSettingIsOwn && !watchIsTayoris

  useEffect(() => {
    // NOTE: scuel_idはGETデータには存在しないので予約済みデータの場合のみpharmacyがあればデフォルト値として再度セットする
    if (prescriptionMedicineSettingIsPharmacy) {
      const isEdit = defaultValues?.id
      const pharmacy = defaultValues?.pharmacy
      if (isEdit && pharmacy) {
        onChangePharmacyHandler(pharmacy, true)
      }
    } else {
      setValue('pharmacy', undefined)
    }
    if (!shouldSelectHowToReceiveMedicine) {
      setValue('how_to_receive_medicine', undefined)
    }
    if (!prescription.setting?.is_todokusuri && !watchIsTayoris) {
      setValue('pharmacy_service', null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prescription.setting, defaultValues])

  useEffect(() => {
    // NOTE: 薬局を選択した後にとどくすりを利用する選択をすると薬局をリセットする
    if (watchPharmacyService) {
      setValue('pharmacy', undefined)
      setValue('scuel_id', undefined)
      onChangePharmacyHandler(undefined)
    }
    setPharmacySelectable(!watchPharmacyService)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchPharmacyService])

  const changeImageHandler =
    (name: string) => (data: ImageProps[], changed?: boolean) => {
      setValue(name, data, {
        shouldValidate: changed,
        shouldDirty: changed,
      })
    }

  const submitDisabled =
    !isValid ||
    (!isPreReservation() && !isDirty) ||
    stripeSubmitting ||
    (!stripeSetting && watchSettlementMethod === 'credit_card')

  const sendingModal = (
    <Modal open={sending || !!sendingContentError} handleClose={() => {}}>
      <SendingContent
        error={sendingContentError}
        onClose={() => {
          setSendingContentError(null)
        }}
      />
    </Modal>
  )

  const pharmacySearchModal = (
    <Modal
      large
      handleClose={() => setShowPharmacySearchModal(undefined)}
      open={!!showPharmacySearchModal}
    >
      <PharmacySearch
        type={
          prescription.setting?.is_todokusuri
            ? 'personal_todokusuri'
            : 'personal'
        }
        personalAddress={getValues('applicant_address')}
        institutionAddress={[
          med.prefecture,
          med.city,
          med.town,
          med.street,
          med.building,
        ].join('')}
        apiKey={prescription.apiKey}
        onSearch={(params) =>
          showPharmacySearchModal === 'search'
            ? prescription.onSearch(params)
            : new Promise((resolve) => resolve(prescription.nearPharmacies))
        }
        onSelect={(pharmacy) => {
          onChangePharmacyHandler(pharmacy)
          setShowPharmacySearchModal(undefined)
        }}
        selectOnly={showPharmacySearchModal === 'preset'}
        onCancel={() => setShowPharmacySearchModal(undefined)}
      />
    </Modal>
  )

  const selectPharmacyContent = (
    <>
      {selectedPharmacy ? (
        <>
          <PharmacyInfo
            pharmacy={selectedPharmacy}
            type={'personal'}
            inLine
            button={
              <DeleteButton
                color={'primary'}
                onClick={() => onChangePharmacyHandler(undefined)}
              />
            }
          />
          <div className={classes.pharmacyDescription}>
            <Text1>
              <Trans
                i18nKey={
                  'trans.処方せんは、受診した医療機関から薬局に送付されます。<br />薬の受け渡し方法・薬の料金の支払い方法等については、直接薬局にご確認ください。'
                }
              />
            </Text1>
          </div>
        </>
      ) : (
        <>
          {prescription.nearPharmacies?.length ? (
            <div className={classes.pharmacySelectButtons}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setShowPharmacySearchModal('preset')}
                disabled={!pharmacySelectable}
              >
                {t('受診先医療機関の近くの薬局から選択')}
              </Button>
            </div>
          ) : null}
          <div className={classes.pharmacySelectButtons}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setShowPharmacySearchModal('search')}
              disabled={!pharmacySelectable}
            >
              {t('薬局を検索')}
            </Button>
            {prescription.setting?.is_todokusuri && (
              <Caption className={classes.pharmacySelectCaption}>
                {t('検索して薬局を指定してください。')}
              </Caption>
            )}
          </div>
        </>
      )}
    </>
  )

  // NOTE: 薬局選択の時の選択肢
  const prescriptionMedicineSettingIsPharmacyContent =
    // NOTE: とどくすりを利用する場合はpharmacy_serviceに"todokusuri"をセットする
    prescription.setting?.is_todokusuri ? (
      <div className={classes.category}>
        <StackItemRadioSelect<string | null>
          alignTop={'22px'}
          defaultValue={defaultValues?.pharmacy_service ?? 'todokusuri'}
          name={'pharmacy_service'}
          label={t('薬局選択')}
          control={control}
          errorMessage={errors?.how_to_receive_medicine?.message}
          values={[
            selectPharmacyOptionTodokusuriValue,
            {
              label: t('お薬を受け取る薬局を指定する'),
              value: '',
              description: (
                <>
                  <div>
                    {t(
                      'ご指定の薬局に対して受診機関が処方箋を FAX します。ご指定の薬局にて直接お薬の受け取りをお願いします。'
                    )}
                  </div>
                  <div className={classes.selectPharmacyContentInSelection}>
                    {selectPharmacyContent}
                  </div>
                </>
              ),
            },
          ]}
          vertical
          required
        />
      </div>
    ) : (
      // NOTE: とどくすりを利用しない場合
      <div className={classes.category}>
        <StackItem
          label={t('薬局選択')}
          alignTop={theme.spacing(2.5)}
          childNoPadding
          required
        >
          <div className={classes.pharmacyContent}>{selectPharmacyContent}</div>
        </StackItem>
      </div>
    )

  // NOTE: 直接配送の時の選択肢
  const prescriptionMedicineSettingIsDirectContent = prescription.setting
    ?.is_todokusuri ? (
    // この時に、デフォルトでpharmacy_serviceにtodokusuriがセットされるようにする
    <div className={classes.category}>
      <StackItemRadioSelect<string | null>
        alignTop={'22px'}
        defaultValue={defaultValues?.pharmacy_service ?? 'todokusuri'}
        name={'pharmacy_service'}
        label={t('薬局選択')}
        control={control}
        errorMessage={errors?.how_to_receive_medicine?.message}
        values={[
          selectPharmacyOptionTodokusuriValue,
          {
            label: howToReceiveMedicineDirectValue,
            value: '',
          },
        ]}
        vertical
        required
      />
    </div>
  ) : (
    <div className={classes.category}>
      <StackItem label={t('薬局選択')}>
        <Text1>
          {t('お近くで薬局にて直接受け取る（処方せんは医療機関から郵送）')}
        </Text1>
      </StackItem>
    </div>
  )

  const tayorisContent = (
    <div className={classes.category}>
      <StackItem label={t('薬局選択')} alignTop>
        <TayorisContent />
      </StackItem>
    </div>
  )

  const insuranceCardForm = settings && (
    <Controller
      name={'insurance_card_images'}
      control={control}
      render={({ field }) => (
        <EditableThumbnailList
          {...field}
          onChangeImages={changeImageHandler('insurance_card_images')}
          images={defaultValues?.insurance_card_images ?? []}
          description={
            <Trans
              i18nKey={
                'trans.保険証、資格確認書を撮影してアップロードしてください。最大5枚まで添付可能です。...'
              }
            />
          }
          injectedErrorMessage={
            errors?.insurance_card_images && t('ファイルを追加してください')
          }
          required={settings.insurance_card_setting === 'required'}
        />
      )}
    />
  )

  return (
    <Layout
      header={
        <AppBarPatient>
          {med.name} {t('予約')}
        </AppBarPatient>
      }
      showFooter
    >
      {sendingModal}
      <Container maxWidth="md">
        <div className={classes.category}>
          <SelectDepartment
            name={'department'}
            value={reservationDepartmentId}
            onSelect={(e) => {
              clearErrors()
              e.target && setReservationDepartmentId(Number(e.target.value))
              onSelectDepartment(e)
            }}
            departments={med.departments}
            fixed={!!defaultValues?.reservation_department_id}
          />
        </div>
        {settings && (
          <form onSubmit={handleSubmit(submitHandler)}>
            {errors?.reservation_department_id && (
              <Caption error>
                {errors?.reservation_department_id.message}
              </Caption>
            )}
            {errors?.access_key && (
              <Caption error>{errors?.access_key.message}</Caption>
            )}
            <Controller
              name={'access_key'}
              render={({ field }) => <input {...field} type="hidden" />}
              control={control}
            />
            <Controller
              name={'reservation_department_id'}
              render={({ field }) => <input {...field} type="hidden" />}
              control={control}
            />
            <div className={classes.category}>
              <StackItem
                label={t('予約日時')}
                description={
                  !defaultValues?.reservation_datetime_start &&
                  t('日時は日本時間表示です。')
                }
                required={!defaultValues?.reservation_datetime_start}
                alignTop={!defaultValues?.reservation_datetime_start && true}
              >
                {watchReservationDatetimeStart && (
                  <Text1 className={classes.reservationDatetimeStart}>
                    {toDateDayTimeStringFromDateTimeString(
                      watchReservationDatetimeStart
                    )}
                    〜（{t('日本時間')}）
                  </Text1>
                )}
                {errors.reservation_datetime_start && (
                  <Caption error>
                    {errors.reservation_datetime_start.message}
                  </Caption>
                )}
              </StackItem>
              {!defaultValues?.reservation_datetime_start && (
                <FormProvider {...formMethods}>
                  <ReservationFrameSelectForm
                    departmentId={reservationDepartmentId}
                    startFormName="reservation_datetime_start"
                    endFormName="reservation_datetime_end"
                    frames={reservationFrames}
                    reservableDates={reservableDates}
                    reservableDeadlineTiming={
                      settings?.reservation_deadline_timing
                    }
                    hasError={!!errors.reservation_datetime_start}
                    onSetDate={setSelectedDate}
                    onChangeCalenderMonth={onChangeCalenderMonth}
                    minDate={jstNewDate}
                    maxDate={getMaxDate(settings)}
                    minDateMessage={t('過去の日付は予約できません')}
                    maxDateMessage={getMaxDateMessage(settings)}
                  />
                </FormProvider>
              )}
              {settings.free_description_field_setting !== 'not' && (
                <StackItemTextField
                  label={t('自由記述欄')}
                  required={
                    settings.free_description_field_setting === 'required'
                  }
                  name={'free_description'}
                  description={
                    <LinkUrlText>
                      {settings?.free_description_supplement || ''}
                    </LinkUrlText>
                  }
                  errorMessage={errors?.free_description?.message}
                  textCounter={400}
                  control={control}
                  alignTop
                  fullWidth
                  multiline
                />
              )}
              {settings.attachment_image_setting !== 'not' && (
                <StackItem
                  label={t('ファイル添付')}
                  labelHelper={<UploadQuestionTooltipMessage />}
                  borderTop
                  alignTop
                  required={settings.attachment_image_setting === 'required'}
                >
                  <Controller
                    name={'attachment_images'}
                    control={control}
                    render={({ field }) => (
                      <EditableThumbnailList
                        required={
                          settings.attachment_image_setting === 'required'
                        }
                        {...field}
                        onChangeImages={changeImageHandler('attachment_images')}
                        images={defaultValues?.attachment_images || []}
                        description={settings.attachment_image_supplement}
                        injectedErrorMessage={
                          errors?.attachment_images &&
                          t('ファイルを追加してください')
                        }
                      />
                    )}
                  />
                </StackItem>
              )}
              {settings.use_in_person_consultation && (
                <StackItemRadioSelect
                  defaultValue={'online'}
                  name={'consultation_method'}
                  label={t('受診方法')}
                  control={control}
                  errorMessage={errors?.consultation_method?.message}
                  values={[
                    { label: t('オンライン'), value: 'online' },
                    { label: t('対面（予約時間に来院）'), value: 'in_person' },
                  ]}
                  borderTop
                  required
                />
              )}
              {watchConsultationMethod !== 'in_person' &&
                (settings.call_methods.filter((m) => m.is_active).length ? (
                  <StackItemRadioSelect
                    defaultValue={
                      getFirstActiveMethod(settings.call_methods)?.name
                    }
                    name={'call_method'}
                    label={t('通話方法')}
                    control={control}
                    errorMessage={errors?.call_method?.message}
                    values={settings.call_methods
                      .filter((m) => m.is_active)
                      .map((m) => {
                        const methodName = t(callMethodLabels[m.name])
                        const tooltip = m.name.startsWith('video_')
                          ? t(
                              '{{methodName}}を使用します。アプリのインストールが必要です。',
                              { methodName }
                            )
                          : undefined
                        return {
                          label: tooltip ? (
                            <QuestionTooltipWithLabel label={t('ビデオ通話')}>
                              {tooltip}
                            </QuestionTooltipWithLabel>
                          ) : (
                            methodName
                          ),
                          value: m.name,
                        }
                      })}
                    borderTop
                    required
                  />
                ) : (
                  <StackItem label={t('連絡事項')} alignTop borderTop>
                    <Text1 className={classes.preWrap}>
                      {settings.reservation_message}
                    </Text1>
                  </StackItem>
                ))}
              {shouldSelectHowToReceiveMedicine && (
                <StackItemRadioSelect
                  borderTop
                  defaultValue={defaultValues?.how_to_receive_medicine}
                  name={'how_to_receive_medicine'}
                  label={t('薬の受け取り方法')}
                  labelHelper={t(
                    '配送を選択した場合、別途配送料が発生する場合があります。'
                  )}
                  control={control}
                  errorMessage={errors?.how_to_receive_medicine?.message}
                  values={howToReceiveMedicineValues.map(
                    ({ value, label }) => ({ value, label: t(label) })
                  )}
                  required
                />
              )}
            </div>
            <div className={classes.category}>
              <StackItem label={t('氏名')} required>
                <span className={textFieldClasses.firstInput}>
                  <Controller
                    name={'applicant_last_name'}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        defaultValue={field.value}
                        variant="outlined"
                        placeholder={placeholder().last_name}
                        error={!!errors?.applicant_last_name}
                        helperText={errors?.applicant_last_name?.message}
                        InputProps={{
                          className: cx(
                            !field?.value && textFieldClasses.required
                          ),
                        }}
                      />
                    )}
                  />
                </span>
                <Controller
                  name={'applicant_first_name'}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      defaultValue={field.value}
                      variant="outlined"
                      placeholder={placeholder().first_name}
                      error={!!errors?.applicant_first_name}
                      helperText={errors?.applicant_first_name?.message}
                      InputProps={{
                        className: cx(
                          !field?.value && textFieldClasses.required
                        ),
                      }}
                    />
                  )}
                />
              </StackItem>
              <StackItem label={t('ふりがな')} required>
                <span className={textFieldClasses.firstInput}>
                  <Controller
                    name={'applicant_last_name_kana'}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        defaultValue={field.value}
                        variant="outlined"
                        placeholder={placeholder().last_name_kana}
                        error={!!errors?.applicant_last_name_kana}
                        helperText={errors?.applicant_last_name_kana?.message}
                        InputProps={{
                          className: cx(
                            !field?.value && textFieldClasses.required
                          ),
                        }}
                      />
                    )}
                  />
                </span>
                <Controller
                  name={'applicant_first_name_kana'}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      defaultValue={field.value}
                      variant="outlined"
                      placeholder={placeholder().first_name_kana}
                      error={!!errors?.applicant_first_name_kana}
                      helperText={errors?.applicant_first_name_kana?.message}
                      InputProps={{
                        className: cx(
                          !field?.value && textFieldClasses.required
                        ),
                      }}
                    />
                  )}
                />
              </StackItem>
              <StackItem label={t('生年月日')} required>
                <Controller
                  name={'applicant_birthday'}
                  control={control}
                  render={({ field }) => (
                    <SelectDate
                      {...field}
                      defaultDate={
                        defaultValues?.applicant_birthday
                          ? parse(
                              defaultValues.applicant_birthday,
                              DateString,
                              new Date()
                            )
                          : new Date(1980, 0, 1)
                      }
                      onChange={(date: string) => {
                        setValue('applicant_birthday', date, {
                          shouldValidate: true,
                          shouldDirty:
                            defaultValues?.applicant_birthday !== date,
                        })
                      }}
                    />
                  )}
                />
              </StackItem>
              {defaultValues?.verification_type === 'sms' ? (
                <StackItem label={t('電話番号')}>
                  <Controller
                    name={'applicant_phone'}
                    render={({ field }) => <input {...field} type="hidden" />}
                    control={control}
                  />
                  <Text1>{defaultValues?.applicant_phone}</Text1>
                </StackItem>
              ) : (
                <StackItemTextField
                  name={'applicant_phone'}
                  label={t('電話番号')}
                  placeholder={placeholder().phone_number}
                  errorMessage={errors?.applicant_phone?.message}
                  control={control}
                  fullWidth
                  required
                />
              )}
              {defaultValues?.verification_type !== 'sms' && (
                <StackItem label={t('メールアドレス')}>
                  <Controller
                    name={'applicant_email'}
                    control={control}
                    render={({ field }) => <input {...field} type="hidden" />}
                  />
                  <Text1>{defaultValues?.applicant_email}</Text1>
                </StackItem>
              )}
              {(settings.applicant_address_setting !== 'not' ||
                watchHowToReceiveMedicine === 'delivery') && (
                <FormProvider {...formMethods}>
                  <ApplicantAddressInputForm
                    required={
                      watchHowToReceiveMedicine === 'delivery' ||
                      settings.applicant_address_setting === 'required'
                    }
                  />
                </FormProvider>
              )}
              {settings.registration_card_number_setting !== 'not' && (
                <StackItemTextField
                  name={'registration_card_number'}
                  label={t('診察券番号')}
                  description={t('お手元にある診察券番号を記入してください。')}
                  errorMessage={errors?.registration_card_number?.message}
                  control={control}
                  alignTop
                  required={
                    settings.registration_card_number_setting === 'required'
                  }
                />
              )}
              {settings.insurance_card_setting !== 'not' && (
                <StackItem
                  label={
                    insuranceType ? t('保険証・マイナ保険証') : t('保険証')
                  }
                  labelHelper={
                    !insuranceType && <UploadQuestionTooltipMessage />
                  }
                  borderTop
                  alignTop
                  required={settings.insurance_card_setting === 'required'}
                >
                  {insuranceType
                    ? myNumberInsuranceSelectable && (
                        <>
                          <RadioSelectContents<InsuranceType>
                            items={
                              [
                                {
                                  label: t('利用しない'),
                                  name: 'not_use',
                                },
                                {
                                  label:
                                    t('保険証、または資格確認書を利用する'),
                                  name: 'insurance_card',
                                  content: insuranceCardForm,
                                },
                                {
                                  label: t('マイナ保険証を利用する'),
                                  name: 'my_number_insurance',
                                  content: (
                                    <div
                                      className={
                                        classes.eligibilityVerification
                                      }
                                    >
                                      <Caption>
                                        {t(
                                          '※下の「閲覧同意へ進む」ボタンをクリックして、オンライン資格確認サイトにて、閲覧同意をしてください。'
                                        )}
                                        <br />
                                        {t(
                                          '※閲覧同意をしないと、予約をすることは出来ません。'
                                        )}
                                      </Caption>
                                      <div>
                                        <EligibilityVerificationButton
                                          setting={
                                            eligibilityVerificationUrlSetting
                                          }
                                          reservationDateString={
                                            watchReservationDatetimeStart
                                          }
                                          isAgreed={watchEligibilityAgreed}
                                        />
                                        <EligibilityVerificationMessageApi
                                          onSuccess={() => {
                                            setValue(
                                              'online_eligibility_verification_agreed',
                                              true
                                            )
                                            clearErrors(
                                              'online_eligibility_verification_agreed'
                                            )
                                            trigger('insurance_card_images')
                                          }}
                                          onError={(errorMessage) => {
                                            setError(
                                              'online_eligibility_verification_agreed',
                                              {
                                                type: 'eligibility_verification_error',
                                                message: errorMessage as string,
                                              }
                                            )
                                          }}
                                        />
                                        {errors?.online_eligibility_verification_agreed && (
                                          <FormHelperText error>
                                            {
                                              errors
                                                .online_eligibility_verification_agreed
                                                .message
                                            }
                                          </FormHelperText>
                                        )}
                                      </div>
                                    </div>
                                  ),
                                },
                              ].filter(
                                // NOTE: 保険証・マイナ保険証が必須でない場合のみ「利用しない」を表示するためにフィルタリング
                                ({ name }) => {
                                  return !(
                                    name === 'not_use' &&
                                    settings.insurance_card_setting ===
                                      'required'
                                  )
                                }
                              ) as RadioSelectContentsProps<InsuranceType>['items']
                            }
                            value={insuranceType}
                            onSelect={(value: InsuranceType) => {
                              setInsuranceType(value)
                              setTimeout(() => {
                                trigger([
                                  'insurance_card_images',
                                  'online_eligibility_verification_agreed',
                                ]).then(() => {
                                  clearErrors([
                                    'insurance_card_images',
                                    'online_eligibility_verification_agreed',
                                  ])
                                })
                              }, 1)
                            }}
                          />
                        </>
                      )
                    : insuranceCardForm}
                </StackItem>
              )}
              {settings.certificate_setting !== 'not' && (
                <StackItem
                  label={t('保険証以外の証書')}
                  labelHelper={<UploadQuestionTooltipMessage />}
                  required={settings.certificate_setting === 'required'}
                  borderTop
                  alignTop
                >
                  <Controller
                    name={'certificate_images'}
                    control={control}
                    render={({ field }) => (
                      <EditableThumbnailList
                        {...field}
                        onChangeImages={changeImageHandler(
                          'certificate_images'
                        )}
                        images={defaultValues?.certificate_images || []}
                        description={t(
                          '障害者手帳など、何かあれば写真を撮影してアップロードしてください。最大5枚まで添付可能です。'
                        )}
                        injectedErrorMessage={
                          errors?.certificate_images &&
                          t('ファイルを追加してください')
                        }
                        required={settings.certificate_setting === 'required'}
                      />
                    )}
                  />
                </StackItem>
              )}
              {settings.identification_document_setting !== 'not' && (
                <StackItem
                  label={t('本人確認書類')}
                  labelHelper={<UploadQuestionTooltipMessage />}
                  required={
                    settings.identification_document_setting === 'required'
                  }
                  borderTop
                  alignTop
                >
                  <Controller
                    name={'identity_verification_images'}
                    control={control}
                    render={({ field }) => (
                      <EditableThumbnailList
                        {...field}
                        onChangeImages={changeImageHandler(
                          'identity_verification_images'
                        )}
                        images={
                          defaultValues?.identity_verification_images || []
                        }
                        description={t(
                          '運転免許証やパスポートなど官公庁が顔写真を貼付した書類を撮影してアップロードしてください。最大5枚まで添付可能です。'
                        )}
                        injectedErrorMessage={
                          errors?.identity_verification_images &&
                          t('ファイルを追加してください')
                        }
                        required={
                          settings.identification_document_setting ===
                          'required'
                        }
                      />
                    )}
                  />
                </StackItem>
              )}
            </div>
            {getFirstActiveMethod(settings?.settlement_settings) && (
              <div className={classes.category}>
                <StackItemRadioSelect
                  required
                  defaultValue={
                    getFirstActiveMethod(settings?.settlement_settings)?.name
                  }
                  name={'settlement_method'}
                  label={t('決済方法')}
                  control={control}
                  errorMessage={errors?.settlement_method?.message}
                  values={settings.settlement_settings
                    .filter((m) => m.is_active)
                    .map((m) => ({
                      label: (
                        <PersonalSettlementsNamesWithLAbel name={m.name} />
                      ),
                      value: m.name,
                    }))}
                />
                {watchSettlementMethod === 'link' && (
                  <StackItem>
                    <Caption>
                      {t('カード情報は、請求金額確定後にご入力いただきます。')}
                    </Caption>
                  </StackItem>
                )}
                {watchSettlementMethod === 'credit_card' && (
                  <>
                    <Controller
                      name={'payment_method_id'}
                      render={({ field }) => <input {...field} type="hidden" />}
                      control={control}
                    />
                    {!!stripeSetting?.payment_method_id && (
                      <StackItem label={t('クレジットカード情報')}>
                        <div className={classes.editCreditButtonWrapper}>
                          <div>
                            {!saveCreditInfo ? (
                              <Text1>{t('設定済')}</Text1>
                            ) : null}
                          </div>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => setSaveCreditInfo((prev) => !prev)}
                          >
                            {!saveCreditInfo ? t('更新') : t('キャンセル')}
                          </Button>
                        </div>
                      </StackItem>
                    )}
                    {stripeSetting && saveCreditInfo && (
                      <Elements
                        stripe={loadStripe(stripeSetting.public_key, {
                          stripeAccount: stripeSetting.connect_account_id,
                        })}
                      >
                        <FormProvider {...formMethods}>
                          <StripeCardInputs
                            secretKey={stripeSetting?.client_secret}
                            posted={stripeSubmitting}
                            onSuccess={onStripePostSuccess}
                            onError={onStripePostError}
                          />
                        </FormProvider>
                      </Elements>
                    )}
                  </>
                )}
              </div>
            )}
            {
              // NOTE: --- 薬局選択関連項目 --
              watchIsTayoris ? ( // NOTE: タヨリスの場合は他の設定値を無視して説明文表示
                tayorisContent
              ) : (
                <>
                  {prescriptionMedicineSettingIsPharmacy &&
                    prescriptionMedicineSettingIsPharmacyContent}
                  {prescriptionMedicineSettingIsDirect &&
                    prescriptionMedicineSettingIsDirectContent}
                  <Controller
                    name={'scuel_id'}
                    render={({ field }) => <input {...field} type="hidden" />}
                    control={control}
                  />
                  {pharmacySearchModal}
                </>
              )
            }
            <div className={classes.category}>
              <div className={classes.submitWrapper}>
                <div className={classes.leftContainer}>
                  {defaultValues?.id && (
                    <Button color="primary" onClick={onCancel}>
                      {t('キャンセル')}
                    </Button>
                  )}
                </div>
                <label className={classes.text}>
                  <Controller
                    name={'saves_data'}
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        {...field}
                        checked={!!field.value}
                        className={classes.topCheckbox}
                        color="primary"
                      />
                    )}
                  />
                  <Text1>{t('記入情報を保存し、次回自動入力する')}</Text1>
                </label>
                <OnMousePopover
                  disabled={!submitDisabled}
                  popover={t('必要条件を満たしていません。確認してください。')}
                >
                  <Button
                    className={cx(classes.submitButton)}
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={submitDisabled}
                  >
                    {t('送信')}
                  </Button>
                </OnMousePopover>
              </div>
            </div>
          </form>
        )}
      </Container>
    </Layout>
  )
}

export default Reservation
